export const KNOWN_ACTION_STATUS = {
  OPEN: 'OPEN',
  BLOCKED: 'BLOCKED',
  COMPLETED: 'COMPLETED',
  CANCELED: 'CANCELED',
} as const;

export type KnownActionStatusCode =
  (typeof KNOWN_ACTION_STATUS)[keyof typeof KNOWN_ACTION_STATUS];

export const ACTION_STATUS_LABEL: Record<KnownActionStatusCode, string> = {
  OPEN: 'Open',
  BLOCKED: 'Blocked',
  COMPLETED: 'Closed',
  CANCELED: 'Withdrawn',
};

export interface ActionReferenceValue {
  code: string;
  label: string;
}

export interface ActionOwner {
  id: string;
  displayName: string;
  isCurrentUser?: boolean;
  email?: string;
}

export interface ActionReferenceData {
  actionTypes: readonly ActionReferenceValue[];
  statuses: readonly ActionReferenceValue[];
  priorities: readonly ActionReferenceValue[];
  communicationPreferences: readonly ActionReferenceValue[];
  dependencyMessage: string | null;
}

export interface ActionOwnerData {
  owners: readonly ActionOwner[];
  dependencyMessage: string | null;
}

export interface CarePlanAction {
  id: string;
  clientId: number;
  caseId: string | null;
  title: string;
  description?: string | null;
  actionTypeCode: string | null;
  assignedToId: string | null;
  statusCode: string | null;
  dueDate: string | null;
  priorityCode: string | null;
  sourceTypeCode: string | null;
  sourceId: number | null;
  communicationPreferenceCode: string | null;
  clientPromise: boolean;
  completedAt: string | null;
  createdAt: string | null;
  updatedAt: string | null;
}

export interface CreateActionValue {
  caseId?: string | null;
  assignedToId?: string;
  title: string;
  description?: string | null;
  actionTypeCode: string;
  dueDate: string;
  priorityCode: string;
  communicationPreferenceCode?: string;
  clientPromise: boolean;
}

export interface UpdateActionValue {
  title?: string;
  description?: string | null;
  actionTypeCode?: string;
  assignedToId?: string;
  statusCode?: string;
  dueDate?: string;
  priorityCode?: string;
  communicationPreferenceCode?: string | null;
  clientPromise?: boolean;
}

export function actionStatusLabel(code: string | null): string {
  if (!code) return 'Status unavailable';
  return (
    ACTION_STATUS_LABEL[code as KnownActionStatusCode] ??
    humanizeActionCode(code)
  );
}

export function humanizeActionCode(code: string | null): string {
  if (!code) return 'Not specified';
  return code
    .toLowerCase()
    .split('_')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

export function isClosedActionStatus(code: string | null): boolean {
  return (
    code === KNOWN_ACTION_STATUS.COMPLETED ||
    code === KNOWN_ACTION_STATUS.CANCELED
  );
}
